import { AxiosError } from 'axios';
import { lazy, Suspense, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CustomError } from 'ts-custom-error';
import { useAuth } from '@steelbuy/auth';
import { useAssociateSoftwareToken, useVerifySoftwareToken, useUserSelfDetails } from '@steelbuy/data-access';

import { ButtonPrimary, InputTextfield, Notification, NotificationLevel } from '@steelbuy/ui-primitive';
import { RoutePath } from '../../router/Routes';
import { FormLayout } from '../../views/layouts/form-layout/FormLayout';
import { ROUTE_ACCOUNT_OVERVIEW } from '../account/Account.enum';
import { PageHeader } from '../page-header/PageHeader';
import './AccountMfaView.scss';

const QRCodeSVG = lazy(() => import('./QrCode'));

interface FormData {
    code: string;
}

export const AccountMfaView = () => {
    const { t } = useTranslation(['translation', 'uiDomain']);

    const navigate = useNavigate();
    const auth = useAuth();
    const { data: user, refetch } = useUserSelfDetails();
    const { control, handleSubmit } = useForm<FormData>();
    const [error, setError] = useState('');
    const mutation = useVerifySoftwareToken();
    const { data } = useAssociateSoftwareToken();

    const onSubmit = handleSubmit(({ code }) => {
        if (!user) {
            throw new CustomError(t('error.noUserError'));
        }
        mutation.mutate(
            {
                accessToken: auth.getToken().getOrThrow(Error('Access Token Unavailable')).accessToken as string,
                username: user.email,
                userCode: code,
            },
            {
                onError: (e: unknown) => {
                    if (e instanceof AxiosError && e.response) {
                        const message = e.response.data?.message;

                        switch (message) {
                            case 'Validation Failed':
                                setError(t('translation:application.accountMFA.validationFailed'));
                                break;
                            case 'Code mismatch':
                                setError(t('translation:application.accountMFA.codeMismatch'));
                                break;
                            default:
                                setError(t('translation:application.accountMFA.genericError'));
                                break;
                        }
                    }
                },
                onSuccess: (verifySoftwareTokenData) => {
                    if (verifySoftwareTokenData.status === 'SUCCESS') {
                        refetch();
                        navigate(RoutePath.ACCOUNT);
                    }
                },
            }
        );
    });

    return (
        <>
            <PageHeader
                pageTitle={t('translation:application.accountMFA.pageTitle')}
                previousPageTitle={t('translation:application.accountMFA.previousPageTitle')}
                onBackClick={() => navigate(ROUTE_ACCOUNT_OVERVIEW)}
            />
            <FormLayout>
                <section className="accountMfaView__section">
                    {t('translation:application.accountMFA.info')}
                    <ol>
                        <li>
                            <span>{t(`uiDomain:login.challengeRespond.mfaSetup.instructions.downloadAndInstall`)}</span>
                        </li>
                        <li>
                            <span>{t(`uiDomain:login.challengeRespond.mfaSetup.instructions.scanQRCode`)}</span>
                        </li>
                        <li>
                            <span>{t(`uiDomain:login.challengeRespond.mfaSetup.instructions.verifyAuthCode`)}</span>
                        </li>
                    </ol>
                </section>
                <Suspense fallback={<div>loading</div>}>
                    {data && (
                        <div className="mfa-setup__qr-code">
                            <QRCodeSVG value={`otpauth://totp/steelbuy.app?secret=${data.secretCode}`} />
                        </div>
                    )}
                </Suspense>
                <form className="accountMfaView__form" onSubmit={onSubmit}>
                    <div>
                        <Controller
                            render={({ field: { onChange, onBlur } }) => (
                                <InputTextfield
                                    label={t('translation:application.accountMFA.inputLabel')}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    placeholder={t(
                                        'uiDomain:login.challengeRespond.softwareTokenMfa.sixDigitCodePlaceholder'
                                    )}
                                />
                            )}
                            name="code"
                            control={control}
                        />
                    </div>
                    <ButtonPrimary label={t('translation:application.accountMFA.submit')} type="submit" />
                </form>
                {error && <Notification level={NotificationLevel.ERROR} message={error} stayOpen />}
            </FormLayout>
        </>
    );
};
